import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { ComponentType } from 'react';

import { buildIconStyleClassString, DEFAULT_ICON_STYLE, type IconStyle } from './iconPicker';
import {
  MATERIAL_ICON_IDS,
  type GeneratedMaterialIconId,
} from './materialIconIds.generated';
import { MATERIAL_ICON_LOADERS } from './materialIconLoaders.generated';
import { POPULAR_MATERIAL_ICON_COMPONENTS } from './popularMaterialIcons';

const MATERIAL_ICON_ATTR_PREFIX = 'material:';

type MaterialIconModule = { default: ComponentType<SvgIconProps> };

/** Normalize Vite/CJS interop so we always get a renderable SvgIcon component. */
function resolveMaterialIconExport(
  loaded: unknown,
): ComponentType<SvgIconProps> | null {
  if (!loaded) return null;
  if (typeof loaded === 'function') {
    return loaded as ComponentType<SvgIconProps>;
  }
  if (typeof loaded === 'object') {
    if ('$$typeof' in loaded) {
      return loaded as ComponentType<SvgIconProps>;
    }
    const mod = loaded as MaterialIconModule;
    if (typeof mod.default === 'function') {
      return mod.default;
    }
    if (mod.default && typeof mod.default === 'object') {
      return resolveMaterialIconExport(mod.default);
    }
  }
  return null;
}

export { MATERIAL_ICON_IDS };

const MATERIAL_ICON_ID_SET = new Set<string>(MATERIAL_ICON_IDS);

const MATERIAL_ICON_BY_ID_LOWER: Record<string, string> = Object.fromEntries(
  MATERIAL_ICON_IDS.map(id => [id.toLowerCase(), id]),
);

type MaterialIconId = GeneratedMaterialIconId;

/** Shown in the picker before the user searches (keeps the grid responsive). */
const POPULAR_MATERIAL_ICON_IDS: readonly string[] = [
  'Inbox',
  'Drafts',
  'Email',
  'Mail',
  'MarkEmailRead',
  'Phone',
  'Call',
  'Sms',
  'LocationOn',
  'Place',
  'Map',
  'Directions',
  'Navigation',
  'MyLocation',
  'Schedule',
  'Event',
  'CalendarToday',
  'Today',
  'AccessTime',
  'Info',
  'Help',
  'HelpOutlineOutlined',
  'CheckCircle',
  'Check',
  'Close',
  'Cancel',
  'Warning',
  'Error',
  'ErrorOutlined',
  'Star',
  'StarBorder',
  'Favorite',
  'FavoriteBorder',
  'Home',
  'Person',
  'People',
  'Group',
  'Search',
  'Download',
  'Upload',
  'Link',
  'AttachFile',
  'Image',
  'Photo',
  'MenuBook',
  'Book',
  'Edit',
  'Create',
  'Add',
  'Remove',
  'Delete',
  'ArrowForward',
  'ArrowBack',
  'ExpandMore',
  'ExpandLess',
  'Visibility',
  'VisibilityOff',
  'Lock',
  'LockOpen',
  'Payment',
  'CreditCard',
  'LocalOffer',
  'Restaurant',
  'LocalCafe',
  'WaterDrop',
  'Hiking',
  'DirectionsBike',
  'DirectionsRun',
  'DirectionsWalk',
  'Park',
  'Forest',
  'Terrain',
  'Flag',
  'EmojiEvents',
  'MilitaryTech',
  'Timer',
  'Alarm',
  'Notifications',
  'Campaign',
  'Share',
  'Print',
  'Save',
  'Refresh',
  'Settings',
  'Build',
  'Construction',
  'HealthAndSafety',
  'MedicalServices',
  'LocalHospital',
  'Wc',
  'Accessible',
  'Pets',
  'Cloud',
  'WbSunny',
  'AcUnit',
  'Thunderstorm',
];

const iconCache = new Map<string, ComponentType<SvgIconProps>>();

const MATERIAL_ICON_BY_TAG_LOWER: Record<string, string> = Object.fromEntries(
  MATERIAL_ICON_IDS.map(id => [`${id.toLowerCase()}icon`, id]),
);

export function isMaterialIconId(value: string): value is MaterialIconId {
  return MATERIAL_ICON_ID_SET.has(value);
}

/** Resolve icon id with correct PascalCase (HTML attributes may change casing). */
export function resolveMaterialIconId(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (isMaterialIconId(trimmed)) return trimmed;
  return MATERIAL_ICON_BY_ID_LOWER[trimmed.toLowerCase()] ?? null;
}

export function materialIconTagName(id: string): string {
  return `${id}Icon`;
}

export function materialIconIdFromTag(tagName: string): string | null {
  const raw = MATERIAL_ICON_BY_TAG_LOWER[tagName.toLowerCase()];
  return raw ?? null;
}

export function materialIconAttrValue(iconId: string): string {
  const resolved = resolveMaterialIconId(iconId);
  return resolved ? `${MATERIAL_ICON_ATTR_PREFIX}${resolved}` : '';
}

export function materialIconIdFromAttr(iconAttr: string): string | null {
  const decoded = decodeURIComponent(iconAttr.trim());
  if (decoded.startsWith(MATERIAL_ICON_ATTR_PREFIX)) {
    return resolveMaterialIconId(decoded.slice(MATERIAL_ICON_ATTR_PREFIX.length));
  }
  if (decoded.startsWith('material/')) {
    return resolveMaterialIconId(decoded.slice('material/'.length));
  }
  return null;
}

export function isMaterialIconTag(tagName: string): boolean {
  return materialIconIdFromTag(tagName) != null;
}

export function getCachedMaterialIcon(iconId: string): ComponentType<SvgIconProps> | null {
  return iconCache.get(iconId) ?? null;
}

/** Resolve lazy loader for an icon id (package imports, not raw node_modules paths). */
export function getMaterialIconLoader(
  iconId: string,
): (() => Promise<MaterialIconModule>) | null {
  const resolved = resolveMaterialIconId(iconId);
  if (!resolved) return null;
  return MATERIAL_ICON_LOADERS[resolved] ?? null;
}

export async function loadMaterialIcon(
  iconId: string,
): Promise<ComponentType<SvgIconProps> | null> {
  const resolved = resolveMaterialIconId(iconId);
  if (!resolved) return null;

  const cached = iconCache.get(resolved);
  if (cached) return cached;

  const popular = POPULAR_MATERIAL_ICON_COMPONENTS[resolved];
  if (popular) {
    iconCache.set(resolved, popular);
    return popular;
  }

  const loader = MATERIAL_ICON_LOADERS[resolved];
  if (!loader) return null;

  try {
    const mod = await loader();
    const Component = resolveMaterialIconExport(mod);
    if (!Component) return null;
    iconCache.set(resolved, Component);
    return Component;
  } catch {
    return null;
  }
}

/** Find Material icon elements (`<icon icon="material:…">` or legacy `<MapIcon>` tags). */
export function findMaterialIconElements(root: ParentNode): HTMLElement[] {
  const elements: HTMLElement[] = [];
  const visit = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    if (tag === 'icon') {
      const iconAttr = element.getAttribute('icon')?.trim() ?? '';
      if (materialIconIdFromAttr(iconAttr)) {
        elements.push(element);
      }
    } else if (isMaterialIconTag(element.tagName)) {
      elements.push(element);
    }
    element.childNodes.forEach(visit);
  };
  root.childNodes.forEach(visit);
  return elements;
}

/** TinyMCE / page HTML for a Material icon (`<icon icon="material:Map" class="icon …">`). */
export function buildMaterialIconHtml(iconId: string, style: IconStyle = DEFAULT_ICON_STYLE): string {
  const resolved = resolveMaterialIconId(iconId);
  if (!resolved) return '';
  const classAttr = buildIconStyleClassString(style);
  return `<icon class="${classAttr}" icon="${materialIconAttrValue(resolved)}"></icon>`;
}

let materialIconValidElementsCache: string | undefined;

/** Legacy MapIcon-style tags for content saved before the unified `<icon>` format. */
export function materialIconValidElements(): string {
  if (!materialIconValidElementsCache) {
    materialIconValidElementsCache = MATERIAL_ICON_IDS.map(
      id => `${materialIconTagName(id)}[*]`,
    ).join(',');
  }
  return materialIconValidElementsCache;
}

export function filterMaterialIconIds(query: string, limit = 150): string[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return POPULAR_MATERIAL_ICON_IDS.filter(id => MATERIAL_ICON_ID_SET.has(id));
  }
  const results: string[] = [];
  for (const id of MATERIAL_ICON_IDS) {
    const tag = materialIconTagName(id).toLowerCase();
    if (id.toLowerCase().includes(q) || tag.includes(q)) {
      results.push(id);
      if (results.length >= limit) break;
    }
  }
  return results;
}
